import { normalizeRoleSlug } from "../../../public/utils/roleAccess";
import { isOfficeRoleSlug } from "./scheduleRoleSlugs";
import {
  SCHEDULE_CONTROL_SUBMODULE_SLUG,
  SCHEDULE_ENTRY_SUBMODULE_SLUG,
} from "./schedulePermissionSlugs";

function submodulePerm(permMap, slug, type) {
  if (!permMap || !slug) return false;
  const row = permMap[slug];
  if (!row || typeof row !== "object") return false;
  return Boolean(row[type]);
}

function permRow(permMap, slug) {
  const row = permMap?.[slug];
  return row && typeof row === "object" ? row : {};
}

/** Schedule Entry screen — User builder `schedule-entry` row only. */
function scheduleEntryStylePerms(row, { isOffice = false } = {}) {
  const canView = Boolean(row.view);
  const canCreate = Boolean(row.create);
  const canEdit = Boolean(row.edit);
  const canDelete = Boolean(row.delete);
  return {
    canView: canView || canCreate,
    canCreate,
    canEdit,
    canDelete,
    canComplete: canEdit,
    canCancel: canEdit,
    canManageMasters: false,
    canDeleteMasters: false,
    canExport: canView || canCreate,
    isAdmin: false,
    isOffice,
  };
}

/** Schedule Control screen — User builder `schedule-control` row. */
function scheduleControlStylePerms(permMap, { isOffice = false } = {}) {
  const row = permRow(permMap, SCHEDULE_CONTROL_SUBMODULE_SLUG);
  const canView = Boolean(row.view);
  const canEdit = Boolean(row.edit);
  const canDelete = Boolean(row.delete);
  return {
    canView,
    canCreate: submodulePerm(permMap, SCHEDULE_ENTRY_SUBMODULE_SLUG, "create"),
    canEdit,
    canDelete,
    canComplete: false,
    canCancel: false,
    canManageMasters: canView && canEdit,
    canDeleteMasters: canDelete,
    canExport: canView,
    isAdmin: canView,
    isOffice,
  };
}

/**
 * Permissions for one schedule screen (User builder submodule row).
 * All roles — including admin — follow User builder only.
 */
export function buildSchedulePermissionsForScreen(
  permMap = {},
  roleSlug = "",
  screenSlug = "",
) {
  const slug = normalizeRoleSlug(screenSlug);
  const isOffice = isOfficeRoleSlug(roleSlug);

  if (slug === SCHEDULE_ENTRY_SUBMODULE_SLUG) {
    return scheduleEntryStylePerms(permRow(permMap, slug), { isOffice });
  }

  if (slug === SCHEDULE_CONTROL_SUBMODULE_SLUG) {
    return scheduleControlStylePerms(permMap, { isOffice });
  }

  return buildSchedulePermissions(permMap, roleSlug);
}

/** Combined schedule permissions from User builder submodule rows. */
export function buildSchedulePermissions(permMap = {}, roleSlug = "") {
  const isOffice = isOfficeRoleSlug(roleSlug);
  const canControl = submodulePerm(permMap, SCHEDULE_CONTROL_SUBMODULE_SLUG, "view");
  const canCreate = submodulePerm(permMap, SCHEDULE_ENTRY_SUBMODULE_SLUG, "create");
  const canView =
    submodulePerm(permMap, SCHEDULE_ENTRY_SUBMODULE_SLUG, "view") ||
    canControl ||
    canCreate;
  const canEdit =
    submodulePerm(permMap, SCHEDULE_ENTRY_SUBMODULE_SLUG, "edit") ||
    submodulePerm(permMap, SCHEDULE_CONTROL_SUBMODULE_SLUG, "edit");
  const canDelete = submodulePerm(permMap, SCHEDULE_CONTROL_SUBMODULE_SLUG, "delete");

  return {
    canView,
    canCreate,
    canEdit,
    canDelete,
    canComplete: canEdit && !canControl,
    canCancel: canEdit && !canControl,
    canManageMasters: canControl && canEdit,
    canDeleteMasters: canDelete && canControl,
    canExport: (canControl && canView) || submodulePerm(permMap, SCHEDULE_ENTRY_SUBMODULE_SLUG, "view"),
    isAdmin: canControl,
    isOffice,
  };
}
